import { frameId } from "../assets/manifest.ts";
import { frameSizeOf } from "../compile/behaviour.ts";
import type { CompileContext } from "../compile/context.ts";
import type { Command } from "../document/commands.ts";
import { COLLISION_CELLS_PER_TILE, type MapDocument, type PropInstance, type TileRef } from "../document/types.ts";
import { alignedToCells, cellStepped } from "./collision-snap.ts";
import type { IssueEntry } from "./issue-entries.ts";
import { keepPropPlacementCommand } from "./panel-commands.ts";
import { contextPropMeasure } from "./prop-align-issues.ts";
import { alignPropsCommand, alignSummary, collides, groupAlignments, movePropsByCommand, offCellProps, type AlignOutcome, type CellGrid, type GroupAlignment, type IssueFix, type PropMove } from "./prop-align.ts";
import type { PropRef } from "./templates.ts";

/**
 * Props against the collision cells (ADR-040 amendment of 2026-10-03, "props stand on the cells", and of
 * 2026-10-04): where a prop goes to stand on them, a step of cells, the props and groups off them, Align props
 * to collision cells and its one-prop fix, Keep as placed, and the fix an Issues line offers, each edit one
 * command.
 */

/**
 * What the alignment needs from the store.
 */
export interface PropAlignmentHost {
  readonly map: () => MapDocument;
  readonly tileSize: () => number;
  readonly context: CompileContext;
  readonly selection: () => readonly PropRef[];
  readonly isEditable: (layerId: string) => boolean;
  readonly run: (command: Command) => boolean;
}

/**
 * What callers reach of the prop aligner, through `EditorStore.propAlignment`.
 */
export type PropAlignmentSlice = Pick<PropAlignment, "alignedAt" | "offCellProps" | "groupAlignments" | "alignToCells" | "alignAlone" | "collides" | "keepPlacement" | "fix">;

/**
 * The prop aligner.
 */
export class PropAlignment {
  /**
   * @param host The store's view of itself.
   */
  public constructor(private readonly host: PropAlignmentHost) {}

  /**
   * Where a prop goes so its frame stands on the collision cells (ADR-040 amendment of 2026-10-03, "props
   * stand on the cells"): the nearest position whose frame corner, the bottom-left or the bottom-right when
   * mirrored, lies on a cell corner (`alignedToCells`), at most half a cell away on each axis. Every prop is
   * placed alike, whether it blocks or not, so a footprint drawn for it later lies under its art. A mirrored
   * prop whose frame nothing sizes cannot be measured and is left where it is.
   *
   * @param prop The prop's frame, anchor and mirror, placed or about to be.
   * @returns The anchor in tiles.
   */
  public alignedAt(prop: Pick<PropInstance, "frame" | "x" | "y" | "flipX">): { x: number; y: number } {
    const width = prop.flipX ? this.frameWidth(prop.frame) : 0;
    return width === undefined ? { x: prop.x, y: prop.y } : alignedToCells(prop, width, prop.flipX, this.host.tileSize(), COLLISION_CELLS_PER_TILE);
  }

  /**
   * Where a prop stands after a step of collision cells (`cellStepped`); a mirrored prop whose frame nothing
   * sizes steps by its left edge.
   *
   * @param prop The prop.
   * @param stepX Cells right.
   * @param stepY Cells down.
   * @returns The anchor in tiles.
   */
  public steppedAt(prop: PropInstance, stepX: number, stepY: number): { x: number; y: number } {
    const width = prop.flipX ? this.frameWidth(prop.frame) : 0;
    return cellStepped(prop, width ?? 0, prop.flipX && width !== undefined, this.host.tileSize(), COLLISION_CELLS_PER_TILE, stepX, stepY);
  }

  /**
   * The props of the map in view that stand off the collision cells, each with the move that would put it
   * alone on them (`offCellProps`): what the Issues list warns of.
   *
   * @returns The moves, in layer order.
   */
  public offCellProps(): readonly PropMove[] {
    return offCellProps(this.host.map(), (frame) => this.frameWidth(frame), this.cellGrid());
  }

  /**
   * How the groups of the map in view stand against the collision cells (`groupAlignments`): props composed
   * together move together, each group by its anchor piece.
   *
   * @param refs When given, only the groups holding one of these props.
   * @returns Each group's alignment.
   */
  public groupAlignments(refs?: readonly PropRef[]): readonly GroupAlignment[] {
    return groupAlignments(this.host.map(), contextPropMeasure(this.host.context, this.cellGrid()), this.cellGrid(), refs);
  }

  /**
   * Align props to collision cells (the author's ruling of 2026-10-03, "Move touching pieces together"): moves
   * every group holding a prop given, else a selected prop, else every group of the map, whose anchor piece
   * stands off the collision cells, each group whole by its anchor's offset, as one command that one undo
   * takes back. A prop brings its whole group, so a selection never splits one. A group with a member on a
   * locked or hidden layer stays whole where it is and is counted. A member whose own corner is off the cells
   * once its group has moved stays so and is counted. A group holding a prop kept as placed stays whole where
   * it is too, and its kept props are counted (ADR-040 amendment of 2026-10-04). Nothing is moved on load.
   *
   * @param refs The props whose groups to align; the prop selection's, or with nothing selected every group of
   *   the map, when omitted.
   * @returns How many props moved in how many groups, the largest move in pixels, how many members of the
   *   groups looked at stay off the cells inside their group and by how much at most, and how many groups off
   *   the cells a locked or hidden layer held back; nothing is recorded when none moved.
   */
  public alignToCells(refs?: readonly PropRef[]): AlignOutcome {
    const gathered = this.host.selection();
    const scope = refs ?? (gathered.length > 0 ? gathered : undefined);
    const groups = this.groupAlignments(scope);
    const free = groups.filter((group) => group.members.every((member) => this.host.isEditable(member.layerId)));
    const held = groups.filter((group) => !free.includes(group) && (group.dx !== 0 || group.dy !== 0)).length;
    const command = alignPropsCommand(this.host.map(), free, this.host.tileSize());
    if (command !== null && !this.host.run(command)) {
      return { aligned: 0, groups: 0, largest: 0, off: 0, offLargest: 0, kept: 0, held };
    }
    return { ...alignSummary(free), held };
  }

  /**
   * Moves one prop alone onto the collision cells, against the pieces it is composed with: the explicit fix
   * for a member that stays off the cells inside its group.
   *
   * @param ref The prop.
   * @returns False when the prop is on the cells, cannot be measured, or a locked or hidden layer refused it.
   */
  public alignAlone(ref: PropRef): boolean {
    const map = this.host.map();
    const command = movePropsByCommand(map, offCellProps(map, (frame) => this.frameWidth(frame), this.cellGrid(), [ref]), this.host.tileSize());
    return command !== null && this.host.run(command);
  }

  /**
   * Whether a prop of the map in view collides, in any state it can be in (`collides`, by the compile
   * context's measure): what decides whether its place against the cells is reported, and so whether Keep as
   * placed is offered for it.
   *
   * @param prop The prop.
   * @returns True when the prop blocks any cell.
   */
  public collides(prop: PropInstance): boolean {
    return collides(prop, contextPropMeasure(this.host.context, this.cellGrid()));
  }

  /**
   * Keeps a prop as placed, or stops keeping it (ADR-040 amendment of 2026-10-04, P1.6-T88), as one command
   * that one undo takes back: a kept prop raises no alignment warning and Align props to collision cells leaves
   * the group it belongs to. The prop does not move, and a later move leaves the mark on it.
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @param keep True to keep the prop as placed, false to stop.
   * @returns False when the prop is already so, is not there, or a locked or hidden layer refused it.
   */
  public keepPlacement(layerId: string, propId: string, keep: boolean): boolean {
    const command = keepPropPlacementCommand(this.host.map(), layerId, propId, keep);
    return command !== null && this.host.run(command);
  }

  /**
   * Makes the fix an Issues line offers (`IssueRemedy`), as one command: `align-prop` aligns the group the
   * line's prop belongs to, `align-prop-alone` moves that prop alone, `keep-prop-placement` keeps the prop as
   * placed. The result says what happened, so the
   * caller can tell the author: a refusal by a locked or hidden layer records nothing and shows as a held
   * group, or as a prop that did not move though it stands off the cells.
   *
   * @param entry The line.
   * @returns What the fix did, or null when the line offers no fix.
   */
  public fix(entry: Pick<IssueEntry, "remedy" | "target">): IssueFix | null {
    if (entry.remedy === null || entry.target.kind !== "prop") {
      return null;
    }
    const ref = { layerId: entry.target.layerId, propId: entry.target.propId };
    if (entry.remedy.id === "align-prop") {
      return { remedy: "align-prop", outcome: this.alignToCells([ref]) };
    }
    if (entry.remedy.id === "keep-prop-placement") {
      return { remedy: "keep-prop-placement", kept: this.keepPlacement(ref.layerId, ref.propId, true) };
    }
    const off = offCellProps(this.host.map(), (frame) => this.frameWidth(frame), this.cellGrid(), [ref])[0];
    return { remedy: "align-prop-alone", moved: this.alignAlone(ref), dx: off?.dx ?? 0, dy: off?.dy ?? 0 };
  }

  /**
   * A frame's width in pixels as the compile context sizes it.
   *
   * @param frame The frame.
   * @returns The width, or undefined when nothing sizes the frame.
   */
  private frameWidth(frame: TileRef): number | undefined {
    return frameSizeOf(this.host.context, frameId(frame.tileset, null, frame.tile))?.w;
  }

  /**
   * The collision cells' geometry for this world: its tile size and the collision resolution.
   *
   * @returns The geometry.
   */
  private cellGrid(): CellGrid {
    return { tileSize: this.host.tileSize(), cellsPerTile: COLLISION_CELLS_PER_TILE };
  }
}
